import { projectStats } from "../data/project-stats";
import { createViewportRendererLifecycle, type ViewportRendererLifecycle } from "../experience/viewport-renderer";
import { deferRendererBoot } from "../experience/defer-renderer";
const hosts = document.querySelectorAll<HTMLElement>("[data-hero-field]");
const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
const fine = matchMedia("(pointer:fine)").matches;
const boot = async () => {
	if (!hosts.length) return;
	const THREE = await import("three");
	hosts.forEach((host) => {
		const canvas = host.querySelector<HTMLCanvasElement>("canvas");
		if (!canvas) return;
		const variant = host.dataset.variant || "timeline";
		const renderer = new THREE.WebGLRenderer({
			canvas,
			alpha: true,
			antialias: false,
			depth: false,
			stencil: false,
			precision: "mediump",
			powerPreference: "high-performance"
		});
		renderer.setPixelRatio(Math.min(devicePixelRatio, 1));
		const scene = new THREE.Scene();
		const camera = new THREE.PerspectiveCamera(38, 1, .1, 100);
		camera.position.set(0, 0, 9.4);
		const root = new THREE.Group();
		scene.add(root);
		const line = new THREE.LineBasicMaterial({
			color: 8363752,
			transparent: true,
			opacity: .52
		});
		const faint = new THREE.LineBasicMaterial({
			color: 7374520,
			transparent: true,
			opacity: .22
		});
		const pointsMaterial = new THREE.PointsMaterial({
			color: 10204402,
			size: .055,
			transparent: true,
			opacity: .72
		});
		const addLines = (values: number[], material = line) => {
			const geo = new THREE.BufferGeometry();
			geo.setAttribute("position", new THREE.Float32BufferAttribute(values, 3));
			const mesh = new THREE.LineSegments(geo, material);
			root.add(mesh);
			return mesh;
		};
		if (variant === "timeline") {
			const nodes: number[] = [];
			const edges: number[] = [];
			for (let i = 0; i < projectStats.phases; i++) {
				const x = -3.7 + i * .62;
				const y = Math.sin(i * .77) * .72;
				const z = Math.cos(i * .49) * .4;
				nodes.push(x, y, z);
				if (i < projectStats.phases - 1) {
					const nx = -3.7 + (i + 1) * .62, ny = Math.sin((i + 1) * .77) * .72, nz = Math.cos((i + 1) * .49) * .4;
					edges.push(x, y, z, nx, ny, nz);
				}
				if (i < projectStats.phases - 3 && i % 2 === 0) {
					const nx = -3.7 + (i + 3) * .62, ny = Math.sin((i + 3) * .77) * .72, nz = Math.cos((i + 3) * .49) * .4;
					edges.push(x, y, z, nx, ny, nz);
				}
			}
			addLines(edges);
			const geo = new THREE.BufferGeometry();
			geo.setAttribute("position", new THREE.Float32BufferAttribute(nodes, 3));
			root.add(new THREE.Points(geo, pointsMaterial));
			root.rotation.z = -.05;
		} else if (variant === "atlas") {
			const stack: number[] = [];
			for (let layerIndex = -2; layerIndex <= 2; layerIndex++) {
				const z = layerIndex * .56;
				for (let i = -4; i <= 4; i++) {
					stack.push(-3.5, i * .48, z, 3.5, i * .48, z, i * .82, -2, z, i * .82, 2, z);
				}
			}
			addLines(stack, faint);
			const nodes: number[] = [];
			for (let i = 0; i < 72; i++) {
				const a = i * .73;
				const r = .5 + i % 9 * .36;
				nodes.push(Math.cos(a) * r, Math.sin(a) * r * .62, (i % 5 - 2) * .3);
			}
			const geo = new THREE.BufferGeometry();
			geo.setAttribute("position", new THREE.Float32BufferAttribute(nodes, 3));
			root.add(new THREE.Points(geo, pointsMaterial));
			root.rotation.x = -.24;
			root.rotation.z = .08;
		} else if (variant === "knowledge") {
			const orbits: number[] = [];
			const nodes: number[] = [];
			for (let ring = 1; ring <= 4; ring++) {
				const rx = 1 + ring * .72, ry = .55 + ring * .4;
				for (let i = 0; i < 80; i++) {
					const a = i / 80 * Math.PI * 2, b = (i + 1) / 80 * Math.PI * 2;
					orbits.push(Math.cos(a) * rx, Math.sin(a) * ry, (ring - 2.5) * .16, Math.cos(b) * rx, Math.sin(b) * ry, (ring - 2.5) * .16);
				}
			}
			addLines(orbits, faint);
			for (let i = 0; i < 48; i++) {
				const ring = 1 + i % 4;
				const a = i * .91;
				nodes.push(Math.cos(a) * (1 + ring * .72), Math.sin(a) * (.55 + ring * .4), (ring - 2.5) * .16);
			}
			const geo = new THREE.BufferGeometry();
			geo.setAttribute("position", new THREE.Float32BufferAttribute(nodes, 3));
			root.add(new THREE.Points(geo, pointsMaterial));
			root.rotation.z = -.14;
		} else if (variant === "control") {
			const grid: number[] = [];
			for (let i = -4; i <= 4; i++) {
				grid.push(-3.2, i * .5, 0, 3.2, i * .5, 0, i * .8, -2.1, 0, i * .8, 2.1, 0);
			}
			addLines(grid, faint);
			const box = [
				-2,
				-1.25,
				.1,
				2,
				-1.25,
				.1,
				2,
				-1.25,
				.1,
				2,
				1.25,
				.1,
				2,
				1.25,
				.1,
				-2,
				1.25,
				.1,
				-2,
				1.25,
				.1,
				-2,
				-1.25,
				.1
			];
			addLines(box, line);
		}
		const baseX = root.rotation.x, baseY = root.rotation.y;
		let targetX = baseX, targetY = baseY, rotX = baseX, rotY = baseY;
		let hostRect: DOMRect | null = null;
		let lifecycle: ViewportRendererLifecycle | null = null;
		const resize = () => {
			hostRect = host.getBoundingClientRect();
			const r = hostRect;
			renderer.setSize(Math.max(1, r.width), Math.max(1, r.height), false);
			camera.aspect = Math.max(1, r.width) / Math.max(1, r.height);
			camera.updateProjectionMatrix();
			lifecycle?.invalidate();
		};
		const ro = new ResizeObserver(resize);
		resize();
		ro.observe(host);
		const onEnter = () => {
			hostRect = host.getBoundingClientRect();
		};
		const onPointer = (event: PointerEvent) => {
			if (!fine || reduced || !hostRect) return;
			targetY = baseY + ((event.clientX - hostRect.left) / Math.max(1, hostRect.width) - .5) * .28;
			targetX = baseX + ((event.clientY - hostRect.top) / Math.max(1, hostRect.height) - .5) * .18;
			lifecycle?.invalidate();
		};
		const onLeave = () => {
			targetX = baseX;
			targetY = baseY;
			lifecycle?.invalidate();
		};
		host.addEventListener("pointerenter", onEnter, { passive: true });
		host.addEventListener("pointermove", onPointer, { passive: true });
		host.addEventListener("pointerleave", onLeave, { passive: true });
		const renderFrame = (_time: number, animate: boolean) => {
			let settling = false;
			if (animate) {
				const dx = targetX - rotX, dy = targetY - rotY;
				rotX += dx * .22;
				rotY += dy * .22;
				root.rotation.x = rotX;
				root.rotation.y = rotY;
				settling = Math.abs(dx) + Math.abs(dy) > .004;
			}
			renderer.render(scene, camera);
			return settling;
		};
		lifecycle = createViewportRendererLifecycle({
			host,
			reducedMotion: reduced,
			render: renderFrame,
			dispose: () => {
				ro.disconnect();
				host.removeEventListener("pointerenter", onEnter);
				host.removeEventListener("pointermove", onPointer);
				host.removeEventListener("pointerleave", onLeave);
				renderer.dispose();
			}
		});
	});
};
deferRendererBoot(hosts, boot);